import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { BilancoHesap } from '../utils/calculations';
import type { TieredQueryResult } from './useTieredQueries';
import type {
  BankalarRow,
  BorclularRow,
  AlacaklilarRow,
  StokMaliyetRow,
  FireHareketleriRow,
  GiderlerRow,
} from '../../../types';
import type { CekRow, KasaOzetRow } from '../utils/calculations';

export function useBilancoData(queries: TieredQueryResult) {
  const { tier1Batch, tier2Batch, getResult } = queries;

  // Bankalar toplamı
  const bankaToplam = useMemo(() =>
    (getResult<BankalarRow>('tier1', 'bankalar')?.data || []).reduce((acc, row) => acc + (row.BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  // Borçlular (Alıcılar) - Varsayılan ve Bağlantı hesapları ayrı
  const borclularVarsayilan = useMemo(() =>
    (getResult<BorclularRow>('tier1', 'borclular')?.data || [])
      .filter(row => row.KOD2 !== 'BAĞLANTI HESABI')
      .reduce((acc, row) => acc + (row.BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  const borclularBaglanti = useMemo(() =>
    (getResult<BorclularRow>('tier1', 'borclular')?.data || [])
      .filter(row => row.KOD2 === 'BAĞLANTI HESABI')
      .reduce((acc, row) => acc + (row.BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  // Alacaklılar (Satıcılar) - Varsayılan ve Bağlantı hesapları ayrı
  const alacaklilarVarsayilan = useMemo(() =>
    (getResult<AlacaklilarRow>('tier1', 'alacaklilar')?.data || [])
      .filter(row => row.KOD2 !== 'BAĞLANTI HESABI')
      .reduce((acc, row) => acc + (row.BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  const alacaklilarBaglanti = useMemo(() =>
    (getResult<AlacaklilarRow>('tier1', 'alacaklilar')?.data || [])
      .filter(row => row.KOD2 === 'BAĞLANTI HESABI')
      .reduce((acc, row) => acc + (row.BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  // Kasa toplamı
  const kasaToplam = useMemo(() =>
    (getResult<KasaOzetRow>('tier1', 'kasa')?.data || []).reduce((acc, row) => acc + (row.GUNCEL_BAKIYE || 0), 0),
    [tier1Batch.data],
  );

  // Alınan çekler toplamı (portföydeki)
  const alinanCeklerToplam = useMemo(() =>
    (getResult<CekRow>('tier1', 'alinanCekler')?.data || [])
      .filter(row => row.CEKDURUM === 'Çek Portföyde')
      .reduce((acc, row) => acc + (row.TUTAR || 0), 0),
    [tier1Batch.data],
  );

  // Verilen çekler toplamı (portföydeki)
  const verilenCeklerToplam = useMemo(() =>
    (getResult<CekRow>('tier1', 'verilenCekler')?.data || [])
      .filter(row => row.CEKDURUM === 'Çek Portföyde')
      .reduce((acc, row) => acc + (row.TUTAR || 0), 0),
    [tier1Batch.data],
  );

  // Stok Maliyet toplamı
  const stokMaliyetToplam = useMemo(() =>
    (getResult<StokMaliyetRow>('tier1', 'stokMaliyet')?.data || []).reduce((acc, row) => acc + (row.TUTAR || 0), 0),
    [tier1Batch.data],
  );

  // Fire toplamı (bu yıl)
  const fireToplam = useMemo(() => {
    const rows = getResult<FireHareketleriRow>('tier2', 'fire')?.data || [];
    const startOfYear = dayjs().startOf('year');
    const endOfYear = dayjs().endOf('year');
    return rows
      .filter(row => {
        const rowDate = dayjs(row.TARIH);
        return rowDate.isAfter(startOfYear.subtract(1, 'day')) && rowDate.isBefore(endOfYear.add(1, 'day'));
      })
      .reduce((acc, row) => acc + (row.CIKANTUTAR || 0), 0);
  }, [tier2Batch.data]);

  // Giderler toplamı (bu yıl)
  const giderlerToplam = useMemo(() => {
    const rows = getResult<GiderlerRow>('tier2', 'giderler')?.data || [];
    const startOfYear = dayjs().startOf('year');
    const endOfYear = dayjs().endOf('year');
    return rows
      .filter(row => {
        const rowDate = dayjs(row.TARIH);
        return rowDate.isAfter(startOfYear.subtract(1, 'day')) && rowDate.isBefore(endOfYear.add(1, 'day'));
      })
      .reduce((acc, row) => acc + (row.NETTUTAR || 0), 0);
  }, [tier2Batch.data]);

  // AKTİF veriler
  const aktifData: BilancoHesap[] = useMemo(() => [
    { id: 'dv', kod: 'I', label: 'DÖNEN VARLIKLAR', tutar: 0, level: 1 },
    { id: 'dv-a', kod: '', label: 'Hazır Değerler', tutar: 0, level: 2 },
    { id: '100', kod: '100', label: 'Kasa', tutar: kasaToplam, level: 3, isClickable: true, navigateTo: '/kasa-raporu' },
    { id: '102', kod: '102', label: 'Bankalar', tutar: bankaToplam, level: 3, isClickable: true, navigateTo: '/bankalar' },
    { id: '101', kod: '101', label: 'Alınan Çekler', tutar: alinanCeklerToplam, level: 3, isClickable: true, navigateTo: '/alinan-cekler' },
    { id: 'dv-c', kod: '', label: 'Ticari Alacaklar', tutar: 0, level: 2 },
    { id: '120', kod: '120', label: 'Alıcılar', tutar: borclularVarsayilan, level: 3, isClickable: true, navigateTo: '/borclular' },
    { id: '120b', kod: '120', label: 'Alıcılar (Bağlantı)', tutar: borclularBaglanti, level: 3, isClickable: true, navigateTo: '/borclular' },
    { id: 'dv-d', kod: '', label: 'Emtia Değerleri', tutar: 0, level: 2 },
    { id: '150', kod: '150', label: 'Stok Değer', tutar: stokMaliyetToplam, level: 3, isClickable: true, navigateTo: '/stok-maliyet' },
  ], [kasaToplam, bankaToplam, alinanCeklerToplam, borclularVarsayilan, borclularBaglanti, stokMaliyetToplam]);

  // PASİF veriler
  const pasifData: BilancoHesap[] = useMemo(() => [
    { id: 'kvyk', kod: 'I', label: 'KISA VADELİ YABANCI KAYNAKLAR', tutar: 0, level: 1 },
    { id: 'kvyk-b', kod: '', label: 'Ticari Borçlar', tutar: 0, level: 2 },
    { id: '320', kod: '320', label: 'Satıcılar', tutar: Math.abs(alacaklilarVarsayilan), level: 3, isClickable: true, navigateTo: '/alacaklilar' },
    { id: '320b', kod: '320', label: 'Satıcılar (Bağlantı)', tutar: Math.abs(alacaklilarBaglanti), level: 3, isClickable: true, navigateTo: '/alacaklilar' },
    { id: '321', kod: '321', label: 'Verilen Çekler', tutar: verilenCeklerToplam, level: 3, isClickable: true, navigateTo: '/verilen-cekler' },
    { id: 'giderler', kod: 'II', label: 'GİDERLER', tutar: 0, level: 1 },
    { id: 'giderler-a', kod: '', label: 'İşletme Giderleri', tutar: 0, level: 2 },
    { id: '770', kod: '770', label: 'Genel Giderler', tutar: giderlerToplam, level: 3, isClickable: true, navigateTo: '/giderler' },
    { id: '771', kod: '771', label: 'Fire Giderleri', tutar: fireToplam, level: 3, isClickable: true, navigateTo: '/fire-raporu' },
  ], [alacaklilarVarsayilan, alacaklilarBaglanti, verilenCeklerToplam, giderlerToplam, fireToplam]);

  // Loading states
  const tier1Loading = tier1Batch.isLoading;
  const tier2Loading = tier2Batch.isLoading;

  return {
    aktifData,
    pasifData,
    tier1Loading,
    tier2Loading,
    bilancoLoading: tier1Loading || tier2Loading,
  };
}
